<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesCommissionFormSection from '../components/SalesCommissionFormSection.vue';
import SalesCommissionNavigation from '../components/SalesCommissionNavigation.vue';
import SalesCommissionPermissionState from '../components/SalesCommissionPermissionState.vue';
import SalesCommissionProgressFooter from '../components/SalesCommissionProgressFooter.vue';
import { useSalesCommissionForm } from '../composables/useSalesCommissionForm';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const id = computed(() => route.params.salesTransactionId);
const commission = useSalesCommissionForm({ id, t, notify: useAlert });

const goBack = () =>
  router.push({
    name: 'operacao_imobiliaria_vendas_cadastro',
    params: { accountId: route.params.accountId, salesTransactionId: id.value },
  });

const complete = async () => {
  if (await commission.save()) goBack();
};
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <LaisHeader
      :title="t('SALES.COMMISSION.TITLE')"
      :subtitle="t('SALES.COMMISSION.SUBTITLE')"
      :breadcrumb="[
        { label: t('SALES.TITLE') },
        { label: t('SALES.REGISTRATION.TITLE') },
        { label: t('SALES.COMMISSION.TITLE') },
      ]"
      show-back
      :back-label="t('SALES.ACTIONS.BACK')"
      @back="goBack"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-refresh-cw"
          :is-loading="commission.loading.value"
          @click="commission.load"
        >
          {{ t('SALES.ACTIONS.REFRESH') }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <SalesCommissionPermissionState
      v-if="commission.forbidden.value"
      class="mx-auto w-full max-w-[1500px] px-4 py-8 sm:px-6 lg:px-10"
      @back="goBack"
    />
    <main
      v-else
      class="mx-auto grid w-full max-w-[1500px] gap-5 px-4 pb-8 sm:px-6 lg:grid-cols-[280px_minmax(0,1fr)] lg:px-10"
    >
      <SalesCommissionNavigation
        :active="commission.activeSection.value"
        :available="commission.availableSections.value"
        :completed="commission.completedSections.value"
        :sections="commission.sections"
        @select="commission.goTo"
      />
      <section class="min-w-0">
        <div
          v-if="commission.loading.value"
          class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
        >
          {{ t('SALES.COMMON.LOADING') }}
        </div>
        <p
          v-else-if="commission.requestError.value"
          class="rounded-lais border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          {{ commission.requestError.value }}
        </p>
        <template v-else>
          <SalesCommissionFormSection
            :active="commission.activeSection.value"
            :form="commission.form.value"
            :errors="commission.errors.value"
            :catalog="commission.catalog.value"
            :payer-options="commission.payerOptions.value"
            :sections="commission.sections"
            @change-count="commission.changeInstallmentCount"
            @distribute-percentages="
              commission.distributeParticipantPercentages
            "
            @update-company="commission.updateCompany"
            @update="commission.updateInstallment"
            @update-payer="commission.updatePayer"
            @update-payer-count="commission.setPayerCount"
            @set-count="commission.setParticipantCount"
            @toggle-group="commission.toggleParticipantGroup"
            @update-group="commission.updateGroup"
            @update-optionist-report="commission.updateOptionistReport"
            @update-participant="commission.updateParticipant"
          />
          <div class="mt-5 flex flex-wrap justify-between gap-3">
            <LaisPrimaryButton
              v-if="commission.canGoBack.value"
              variant="ghost"
              icon="i-lucide-arrow-left"
              @click="commission.previous"
            >
              {{ t('SALES.COMMISSION.ACTIONS.PREVIOUS') }}
            </LaisPrimaryButton>
            <span v-else />
            <div class="flex gap-3">
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-save"
                :is-loading="commission.saving.value"
                @click="commission.save"
              >
                {{ t('SALES.COMMISSION.ACTIONS.SAVE') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-if="commission.canGoNext.value"
                icon="i-lucide-arrow-right"
                :is-loading="commission.saving.value"
                @click="commission.next"
              >
                {{ t('SALES.COMMISSION.ACTIONS.NEXT') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                v-else
                icon="i-lucide-check"
                :is-loading="commission.saving.value"
                @click="commission.save"
              >
                {{ t('SALES.COMMISSION.ACTIONS.COMPLETE') }}
              </LaisPrimaryButton>
            </div>
          </div>
        </template>
      </section>
    </main>
    <SalesCommissionProgressFooter
      v-if="!commission.forbidden.value"
      :progress="commission.progress.value"
      :saving="commission.saving.value"
      @complete="complete"
    />
  </LaisGamificationSurface>
</template>
